<script lang="ts">
	import { businessTimezone } from '#lib/stores/time.js';
	import { goto } from '$app/navigation';
	import { page } from '$app/state';
	import Gtm from '#lib/component/analytics/Gtm.svelte';
	import ChatSupport from '#lib/component/navigation/ChatSupport.svelte';
	import { activityStore } from '#lib/stores/activity.js';
	import { initPublicApi, isApiReady } from '#lib/stores/api.js';
	import { t } from '#lib/stores/i18n.js';
	import { isMobile } from '#lib/stores/mobile.js';
	import { offerStore } from '#lib/stores/offer.js';
	import { packageStore } from '#lib/stores/package.js';
	import Loading from '#lib/svg/loading.svg.svelte';
	import WidgetDecorator from '#lib/widget/components/WidgetDecorator.svelte';
	import { hydrateWidgetContent, OfferEngineType } from '#lib/types/generated/index.js';
	import { activateEventFlow, widgetSetFlow } from '#lib/widget/booking/flow.js';
	import { mapWidgetActivityToActivity, mapWidgetOfferToOffer } from '#lib/widget/mapper.js';
	import { loadMember } from '#lib/widget/member.js';
	import {
		setDefaultWidgetContent,
		widgetChatEnabled,
		widgetConfig,
		widgetContent
	} from '#lib/widget/core.js';
	import { onMount, untrack, type Snippet } from 'svelte';
	import type { PageData } from './$types';

	interface Props {
		data: PageData;
		children?: Snippet;
	}

	let { data, children }: Props = $props();

	function syncData(d: PageData) {
		$businessTimezone = d.widgetContent.business.timezone;
		const content = hydrateWidgetContent(d.widgetContent);
		$widgetConfig = content.config;
		setDefaultWidgetContent(content);
		$packageStore = content.packages;
		$activityStore = content.activities.map(mapWidgetActivityToActivity);
		$offerStore = Object.values(content.offers).map(mapWidgetOfferToOffer);

		const flowParam = page.url.searchParams.get('flow');
		widgetSetFlow(
			flowParam === 'flow1' || flowParam === 'flow2' || flowParam === 'flow3'
				? flowParam
				: d.widgetConfig.flowSelected
		);

		const allOffers = Object.values(content.offers);
		if (allOffers.length > 0 && allOffers.every((o) => o.engine === OfferEngineType.Event)) {
			activateEventFlow(true);
		}
	}

	untrack(() => syncData(data));

	let contractIsActive = $derived(data.contractActive);

	let loaded = $state(false);
	onMount(() => {
		initPublicApi();
		if (!contractIsActive) {
			goto('/widget/' + $widgetConfig.hash + '/inactive');
			loaded = true;
			return;
		}

		loadMember().finally(() => (loaded = true));
	});

	let width = $state(0);
	$effect(() => {
		$isMobile = width < 640;
	});
</script>

<svelte:window bind:innerWidth={width} />
<svelte:head>
	<title>{$t('widget-external.page_title', { businessName: $widgetContent.business.name })}</title
	>
	<link rel="manifest" href="/widget/{$widgetConfig.hash}/manifest.json" />
	<link rel="icon" href="/favicon.ico" />
</svelte:head>

<Gtm
	gtmId={['GTM-W2CGSD4B'].concat(
		$widgetConfig.googleTagManagerId ? [$widgetConfig.googleTagManagerId] : []
	)}
/>

<div
	id="app-layout"
	class="flex flex-1 h-dvh fixed w-full overflow-hidden {$widgetConfig.theme.backgroundImage ||
	$widgetConfig.theme.pageBackgroundColor
		? 'md:bg-transparent bg-widget-background dark:bg-widget-background-dark'
		: 'bg-widget-background dark:bg-widget-background-dark'} text-widget-background-on dark:text-widget-background-dark-on"
>
	<WidgetDecorator config={$widgetConfig}>
		{#if !$isApiReady}
			<Loading className="flex-none p-5 mt-4 mx-auto" width="w-12" />
		{:else if loaded && (contractIsActive || page.url.pathname === '/widget/' + $widgetConfig.hash + '/inactive')}
			<div>
				{@render children?.()}
			</div>
		{/if}
	</WidgetDecorator>
</div>
{#if $widgetChatEnabled}
	<ChatSupport />
{/if}
